import * as impl from './impl.ts';
import { authenticateSecret, Authenticator } from './core.ts';
import { randomId } from '../common/ids.ts';
import { sign, unsign } from '../common/jwt.ts';
import type { Authentication, AuthenticationRequest, AuthenticationResponse } from './types.ts';
import { exceptionInfo, exceptionMessage } from '../common/types.ts';
import { getLogger } from '../logger.ts';

const log = getLogger('gateway.auth.basic');

async function authenticate({authentication}: AuthenticationRequest,
                            secret: string,
                            ttl: number,
                            secretVerifier?: (username?: string, password?: string) => Promise<boolean>,
                            usernameResolver?: (login?: string) => Promise<string> | string): Promise<AuthenticationResponse> {
    switch (authentication?.method) {
        case 'secret': {
            const login = authentication.login;
            const resolvedUsername = usernameResolver ? await usernameResolver(login) : login;
            if (secretVerifier) {
                if (!(await secretVerifier(resolvedUsername, authentication.secret))) {
                    throw exceptionInfo("Invalid login/secret", {type: 'failure', message: 'Invalid login/secret'});
                }
            }
            const response = await authenticateSecret(authentication, resolvedUsername);
            const accessToken = sign({
                user: response.user,
                exp: Math.floor((Date.now() + ttl) / 1000 )
            }, secret);
            return {...response, accessToken}

        }
        case 'access-token': {
            try {
                const token = (authentication as Required<Pick<Authentication, 'token'>>).token;
                const user = (unsign(token, secret)).user as string | undefined;
                // also consider now/ttl
                return {type: 'success', login: user, user, accessToken: token};
            }
            catch (e) {
                throw exceptionInfo(`Invalid or expired token:${exceptionMessage(e)}`, {type: 'failure', message: 'Invalid or expired token: ' + exceptionMessage(e)}, e);
            }
        }
        default: {
            const msg = `Unknown authentication method '${authentication?.method}'`;
            log.debug(msg);
            throw exceptionInfo(msg, {type: 'failure', message: msg});
        }
    }
}

class BasicAuthenticator implements impl.AuthImpl {
    readonly #secret: string;
    readonly #ttl: number;
    readonly #secretVerifier?: (user?: string, password?: string) => Promise<boolean>;
    readonly #usernameResolver?: (login?: string) => Promise<string> | string;

    constructor(
        secret: string,
        ttl: number,
        secretVerifier?: (username?: string, password?: string) => Promise<boolean>,
        usernameResolver?: (login?: string) => Promise<string> | string) {
        this.#secret = secret;
        this.#ttl = ttl;
        this.#secretVerifier = secretVerifier;
        this.#usernameResolver = usernameResolver;
    }

    auth(authenticationRequest: AuthenticationRequest) {
        return authenticate(authenticationRequest, this.#secret, this.#ttl, this.#secretVerifier, this.#usernameResolver);
    }
}

export interface BasicAuthenticatorConfig extends impl.DefaultAuthenticatorConfig {
    ttl?: number,
    secretVerifier?: (login?: string, secret?: string) => Promise<boolean>,
    usernameResolver?: (login?: string) => Promise<string> | string,
    /**
     * When false (default), usernames are converted to lowercase for case-insensitive comparison.
     * When true, the original case of usernames is preserved.
     * Lowercasing is applied before the usernameResolver if one is provided.
     */
    usernameCaseSensitive?: boolean
}

/**
 * Converts a string to lowercase using locale-insensitive comparison (equivalent to Java's Locale.ROOT).
 */
function toLowerCaseInvariant(str?: string): string {
    return str?.toLowerCase() ?? '';
}

export function authenticator(config: BasicAuthenticatorConfig): Authenticator {
    const ttl = 1000 * (config.ttl ?? 60000);
    const secretVerifier = config.secretVerifier;
    const caseSensitive = config.usernameCaseSensitive ?? false;
    const customResolver = config.usernameResolver;

    // Build the username resolver chain: lowercase first (if not case-sensitive), then custom resolver
    let usernameResolver: ((login?: string) => Promise<string> | string) | undefined;
    if (!caseSensitive && customResolver) {
        usernameResolver = async (login) => {
            const resolved = await customResolver(toLowerCaseInvariant(login));
            return toLowerCaseInvariant(resolved);
        };
    }
    else if (!caseSensitive) {
        usernameResolver = toLowerCaseInvariant;
    }
    else {
        usernameResolver = customResolver;
    }

    if (log.enabledFor('debug')) {
        log.debug(`creating basic authenticator: ttl: ${ttl}, secret verifier: ${secretVerifier !== undefined}, username resolver: ${usernameResolver !== undefined}, case-sensitive: ${caseSensitive}`);
    }

    return impl.authenticator(config, new BasicAuthenticator(randomId(), ttl, secretVerifier, usernameResolver));
}
